import { HttpClient } from "@angular/common/http";
import { inject, Injectable, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { firstValueFrom } from "rxjs";
import { Device } from "../devices";
import { Integration } from "../integrations";
import { License } from "../licenses";
import { User } from "../users";
import { NpiRegistrySummaryResource } from "./npi-registry.model";
import { Organization, OrganizationDirectSettings } from "./organization.model";

export interface OrganizationActivationStatusRequest {
    id: Organization["id"];
    activated: boolean;
    seedRecords?: boolean;
    sendEmail?: boolean;
    resyncSesOrganization?: boolean;
}

export interface AddOrgAssignmentRequest {
    organizationId: string;
    userId: string;
    jobFunctionId: number;
    role: string;
}

export interface RemoveOrgAssignmentRequest {
    organizationId: string;
    userId: string;
}

@Injectable({
    providedIn: "root"
})
export class AdminOrganizationsService {
    private readonly httpClient = inject(HttpClient);

    public updateDirectSettings(organizationId: Organization["id"], organizationDirectSettings: OrganizationDirectSettings): Promise<void> {
        return firstValueFrom(this.httpClient.put<void>(`/api/admin/organizations/${organizationId}/settings/direct`, organizationDirectSettings));
    }

    public isSubdomainValidAsync(organizationId?: Organization["id"], subdomain?: string): Promise<boolean> {
        return firstValueFrom(this.httpClient.get<boolean>(`/api/organizations/subdomain/isvalid`, { params: { organizationId, subdomain } }));
    }

    public addOrganization(organization: Organization): Promise<Organization> {
        return firstValueFrom(this.httpClient.post<Organization>(`/api/organizations`, organization));
    }

    public deleteOrganization(organizationId: Organization["id"]): Promise<void> {
        return firstValueFrom(this.httpClient.delete<void>(`/api/organizations/${organizationId}`));
    }

    public clearCancellation(organizationId: Organization["id"]): Promise<void> {
        return firstValueFrom(this.httpClient.post<void>(`/api/organizations/${organizationId}/clearcancel`, null));
    }

    public toggleEnabled(organizationId: Organization["id"]): Promise<void> {
        return firstValueFrom(this.httpClient.put<void>(`/api/organizations/${organizationId}/enable`, null));
    }

    public updateActivationStatus(request: OrganizationActivationStatusRequest): Promise<void> {
        return firstValueFrom(this.httpClient.put<void>(`/api/organizations/${request.id}/activate`, request));
    }

    public syncWithIntercom(organizationId: Organization["id"]): Promise<void> {
        return firstValueFrom(this.httpClient.post<void>(`/api/organizations/${organizationId}/sync`, null));
    }

    public toggleApiCapture(organizationId: Organization["id"]): Promise<void> {
        return firstValueFrom(this.httpClient.put<void>(`/api/organizations/${organizationId}/apicapture`, null));
    }

    public updateOrganization(organization: Organization): Promise<Organization> {
        return firstValueFrom(this.httpClient.put<Organization>(`/api/organizations/${organization.id}`, organization));
    }

    public npiSearchByNumber(npiNumber: string): Promise<NpiRegistrySummaryResource> {
        return firstValueFrom(
            this.httpClient.get<NpiRegistrySummaryResource>("/api/admin/organizations/npi", {
                params: { npiNumber }
            })
        );
    }
    public addOrgAssignment(request: AddOrgAssignmentRequest): Promise<void> {
        return firstValueFrom(this.httpClient.post<void>(`/api/admin/organizations/${request.organizationId}/users`, request));
    }

    public deleteOrgAssignment(organizationId: string, userId: string): Promise<void> {
        return firstValueFrom(this.httpClient.delete<void>(`/api/admin/organizations/${organizationId}/users/${userId}`));
    }

    public getOrganizationAdministrators(organizationId: string): Signal<User[]> {
        return toSignal(this.httpClient.get<User[]>(`/api/admin/organizations/${organizationId}/admins`), { initialValue: [] });
    }

    public getOrganizationIntegrations(organizationId: string): Signal<Integration[]> {
        return toSignal(this.httpClient.get<Integration[]>(`/api/organizations/${organizationId}/integrations`), { initialValue: [] });
    }

    public getOrganizationDevices(organizationId: string): Signal<Device[]> {
        return toSignal(this.httpClient.get<Device[]>(`/api/admin/organizations/${organizationId}/devices`), { initialValue: [] });
    }

    public getOrganizationLicenses(organizationId: string): Signal<License[]> {
        return toSignal(this.httpClient.get<License[]>(`/api/admin/organizations/${organizationId}/licenses`), { initialValue: [] });
    }
}
